import { Loader, Lock } from 'lucide-react'
import { UseFormReturn } from 'react-hook-form'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Image } from 'src/shared/components/ui'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { BusinessOAuthProvider } from '../consts'
import { AuthenticateFormData } from '../types'
import { CheckEmailSuccess } from './CheckEmailSuccess'
import { EmailForm } from './EmailForm'
import { HeaderSection } from './HeaderSection'
import { LoginSignupToggle } from './LoginSignupToggle'
import { SocialAuthButtons } from './SocialAuthButtons'
import { TermsSection } from './TermsSection'

export const LeftColumn = ({
  methods,
  onSubmit,
  loading,
  oauthLoading,
  isOAuthReturn,
  onOAuth,
  isCompanyUrlValid,
  isLoginMode,
  onLoginModeChange,
  magicLinkSent,
  sentToEmail,
  onResetMagicLink,
  signupTitle,
  signupDescription,
  loginDescription,
  compact = false,
  formOnly = false,
}: {
  methods: UseFormReturn<AuthenticateFormData>
  onSubmit: (data: AuthenticateFormData) => void
  loading: boolean
  oauthLoading: boolean
  isOAuthReturn: boolean
  onOAuth: (provider: BusinessOAuthProvider) => void
  isCompanyUrlValid: boolean
  isLoginMode: boolean
  onLoginModeChange: (login: boolean) => void
  magicLinkSent: boolean
  sentToEmail: string
  onResetMagicLink: () => void
  signupTitle?: string
  signupDescription?: string
  loginDescription?: string
  compact?: boolean
  formOnly?: boolean
}) => {
  const { t } = useLocalization()
  const showCheckEmail = isLoginMode && magicLinkSent
  const busy = loading || oauthLoading

  const authFields = isOAuthReturn ? (
    <Column className="items-center gap-3 py-8">
      <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
      <p>{t('Signing you in...', 'Loggar in dig...')}</p>
    </Column>
  ) : showCheckEmail ? (
    <CheckEmailSuccess
      email={sentToEmail}
      onUseDifferentEmail={onResetMagicLink}
    />
  ) : (
    <Column className="w-full gap-4">
      <Row className="items-center justify-end gap-2">
        <p className="text-muted-foreground">
          {isLoginMode
            ? t('New here?', 'Ny här?')
            : t('Have an account?', 'Har ni redan konto?')}
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onLoginModeChange(!isLoginMode)}
        >
          {isLoginMode
            ? t('Sign up', 'Registrera dig')
            : t('Log in', 'Logga in')}
        </Button>
      </Row>
      <SocialAuthButtons loading={busy} onProviderClick={onOAuth} />
      <EmailForm
        methods={methods}
        onSubmit={onSubmit}
        loading={busy}
        isCompanyUrlValid={isCompanyUrlValid}
        isLoginMode={isLoginMode}
        emailLabel={t('Work email', 'Arbetsmejl')}
        emailPlaceholder="name@company.com"
        submitLabel={t('Continue with email', 'Fortsätt med e-post')}
      />
    </Column>
  )

  if (formOnly) {
    return authFields
  }

  if (compact) {
    return (
      <Column className="min-w-0 flex-1 justify-between overflow-y-auto bg-white px-5 py-6 sm:px-10 sm:py-10">
        <Column className="w-full max-w-md gap-7">
          <Row className="items-center justify-between gap-3 flex-wrap">
            <Image
              src={IMAGE_URLS.logos.klayder}
              alt="Klayder"
              className="h-7 w-auto"
            />
            <Row className="items-center gap-2">
              <p className="text-muted-foreground">
                {isLoginMode
                  ? t('New here?', 'Ny här?')
                  : t('Have an account?', 'Har ni redan konto?')}
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onLoginModeChange(!isLoginMode)}
              >
                {isLoginMode
                  ? t('Sign up', 'Registrera dig')
                  : t('Log in', 'Logga in')}
              </Button>
            </Row>
          </Row>

          {isOAuthReturn ? (
            <Column className="items-center gap-3 py-8">
              <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
              <p>{t('Signing you in...', 'Loggar in dig...')}</p>
            </Column>
          ) : showCheckEmail ? (
            <CheckEmailSuccess
              email={sentToEmail}
              onUseDifferentEmail={onResetMagicLink}
            />
          ) : (
            <Column className="w-full gap-6">
              <Column className="gap-1.5">
                <h1 className="!leading-8">
                  {isLoginMode
                    ? t('Welcome back', 'Välkommen tillbaka')
                    : signupTitle ??
                      t("Let's get started", 'Låt oss komma igång')}
                </h1>
                <p className="text-muted-foreground">
                  {isLoginMode
                    ? loginDescription ??
                      t(
                        'Log in to access your mockups, quotes and ongoing kits.',
                        'Logga in för att komma åt era mockups, offerter och pågående kit.'
                      )
                    : signupDescription ??
                      t(
                        'Sign up to access your mockups, quotes and ongoing kits.',
                        'Registrera er för att komma åt era mockups, offerter och pågående kit.'
                      )}
                </p>
              </Column>
              <SocialAuthButtons loading={busy} onProviderClick={onOAuth} />
              <EmailForm
                methods={methods}
                onSubmit={onSubmit}
                loading={busy}
                isCompanyUrlValid={isCompanyUrlValid}
                isLoginMode={isLoginMode}
                emailLabel={t('Work email', 'Arbetsmejl')}
                emailPlaceholder="name@company.com"
                submitLabel={t('Continue with email', 'Fortsätt med e-post')}
              />
            </Column>
          )}
        </Column>

        <Row className="items-center gap-2 pt-8">
          <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            {t(
              'We only use your email to get you into your workspace.',
              'Vi använder bara er e-post för att släppa in er i arbetsytan.'
            )}
          </p>
        </Row>
      </Column>
    )
  }

  return (
    <Column className="flex-1 px-8 py-6 lg:px-10">
      <Column className="w-full max-w-md gap-5">
        <Image
          src={IMAGE_URLS.logos.klayder}
          alt="Klayder"
          className="h-8 w-auto"
        />

        <Column className="w-full gap-4">
          <LoginSignupToggle
            isLoginMode={isLoginMode}
            onLoginModeChange={onLoginModeChange}
          />

          {isOAuthReturn ? (
            <Column className="items-center gap-3 py-8">
              <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
              <p>{t('Signing you in...', 'Loggar in dig...')}</p>
            </Column>
          ) : (
            <>
              {!showCheckEmail && (
                <HeaderSection
                  isLoginMode={isLoginMode}
                  signupTitle={signupTitle}
                  signupDescription={signupDescription}
                />
              )}

              {showCheckEmail ? (
                <CheckEmailSuccess
                  email={sentToEmail}
                  onUseDifferentEmail={onResetMagicLink}
                />
              ) : (
                <>
                  <SocialAuthButtons loading={busy} onProviderClick={onOAuth} />
                  <EmailForm
                    methods={methods}
                    onSubmit={onSubmit}
                    loading={busy}
                    isCompanyUrlValid={isCompanyUrlValid}
                    isLoginMode={isLoginMode}
                  />
                </>
              )}
            </>
          )}
        </Column>

        <TermsSection />
      </Column>
    </Column>
  )
}
